/**
 * The collections the panel edits, one entry each — the single place that says
 * a collection exists, where its pages live, what to call it, and what is
 * special about it.
 *
 * Before this file the same facts were spread over six files: the API's
 * allow-list, the neighbours a reference check needs, the URL prefix (twice),
 * the nav, the dashboard's list of collections, the list screen's titles and
 * the SEO screen's default-title rule. Adding a collection meant finding all of
 * them, and missing one meant a screen that half-worked. Now it is one entry
 * here plus a `FORMS` layout in forms.js.
 *
 * Shared by the browser and the API, like forms.js, so the nav and the router
 * cannot disagree about what exists.
 *
 * ## The fields of an entry
 *
 *   slug               the collection's file name under content/collections/
 *   one, many, blurb   what the screens call it
 *   urlPrefix          where an Item's page is served, frozen by ADR-0003 —
 *                      or `null` for a collection whose Items have no page of
 *                      their own (a FAQ appears on other pages). A pageless
 *                      collection gets no redirect on unpublish, no `live:`
 *                      link, no SEO row, and the form shows `whereShown`
 *                      instead of an address.
 *   whereShown(fields, refs)   for a pageless collection, the path of the page
 *                      that shows the Item; used as the preview path too.
 *                      Given the Item's `fieldData` and, on the server, the
 *                      reference index (`collections`, `byId`) — the browser's
 *                      list screen passes nothing, and gets null when the
 *                      answer needs the neighbours.
 *   placements(fields, refs)   the same, in full: every page that shows the
 *                      Item, `{url, title}[]` in the site's order. When an
 *                      entry gives this and not `whereShown`, the first page
 *                      is where it is shown.
 *   defaultDestination where an unpublished page's 301 sends people unless
 *                      the person says otherwise.
 *   seoRows            false for a collection whose pages are `noindex` by
 *                      pattern (lib/indexability.js): they have a URL but no
 *                      head worth editing, so the SEO screen leaves them out.
 *   neighbours         the collections that have to be loaded alongside this
 *                      one because its Reference fields point into them and
 *                      `checkItem` resolves every reference. Listed rather than
 *                      derived from the schema for one reason: `article.json`
 *                      is 1.1 MB, and loading every collection to save a
 *                      Provider's telephone number would make the panel feel
 *                      like the thing it replaced.
 *   nameLabel          what the form calls the `name` field
 *   createLabel, createPlaceholder   the new-item dialog's prompt
 *   seoTitle(fieldData)   the <title> the site prints when `seo-title` is empty
 *   descriptionField   the field the site prints as the meta description
 *   listColumns        extra columns on the list screen, in order:
 *                      `{ key, label, kind: "option"|"date", field? }` where
 *                      `key` is the row-summary key and `field` the schema
 *                      field it is read from (and, for an Option column, the
 *                      field whose options name the value); `key` itself when
 *                      `field` is not given. The API fills the row from this.
 *   optionFilter       `{ field, label, every }` — an Option field the list
 *                      can be filtered by, its choices read off the schema;
 *                      the row carries the field under its own name
 *   reorder            `{ field, groupBy, title, help, pickLabel?, groups?,
 *                      member?, compare?, emptyText? }` — drag-to-order
 *                      support: `field` is the Number written, `groupBy` the
 *                      field the list is split by; the row carries both. By
 *                      default `groupBy` is an Option field, its options are
 *                      the groups and a row is in the group its value names —
 *                      a Provider's discipline. The four hooks are for a
 *                      group that is not an option: `groups(data)` lists them
 *                      as `{ id, name }` off the list payload, `member(row,
 *                      groupId, schema)` says whether a row is in one,
 *                      `compare(a, b)` is the order the site shows the group
 *                      in today (the card must start from the page as it is,
 *                      not from a guess), and `emptyText` is the line for a
 *                      group with nothing in it. A row can be in several
 *                      groups (a question on two assessment pages) and the
 *                      card says so, since one `field` is one position on
 *                      every page. Read through `reorderGroups`,
 *                      `reorderMembers` and `reorderElsewhere` below.
 *   imageFields        the Image fields tried, in order, for a row's thumbnail
 *   mandatory, rules   the clinic's own publish-time checks (forms.js)
 *   decorative         Image fields the template prints with `alt=""`, so no
 *                      alt text is asked for (forms.js `decorative: true`)
 *   copyExcludes       fieldData keys a "copy from" create never carries over:
 *                      what identifies the source, dates it, or is true of one
 *                      page only (its canonical URL, its search title, its
 *                      place in a list)
 *   createDefaults(collection, fields, existingItems, schema)   extra fieldData
 *                      a new Item starts with, filled only where still empty;
 *                      `schema` is there for an Option's id
 *   createSlug(name, existingItems)   the address a new Item gets when the
 *                      dialog does not say — null for the usual slugify(name)
 *   beforeSave(fieldData, existing)   the fieldData as it is written, after
 *                      the form's values are on it: the place for one field
 *                      to follow another on every save
 *   mandatoryForPreview   fields a Preview needs filled, not only a Publish —
 *                      a preview deployment is public, and a review without
 *                      its consent record must not reach one
 *   pins               where the site pins Items of this collection by slug,
 *                      for the refusal to unpublish or archive one of them:
 *                      `{ file, slugs(json), where, way }[]` — the content
 *                      file, the slugs it pins (tolerant of a malformed
 *                      file), and the two halves of the refusal ("pinned
 *                      <where>", then what to do: <way>). Every slug a pins
 *                      file names must be Live or check-content fails the
 *                      build (tools/check-content.mjs PINS), so this list
 *                      follows PINS. Empty when nothing pins the collection
 *   unpublishReasons   the reasons the unpublish prompt offers, for whoever
 *                      reads the redirect entry in a year; a person can still
 *                      type their own
 *
 * ## What the list screen, the form's head and the dashboard read
 *
 *   listNameLabel      the list's first column header ("Reviewer", "Question")
 *   title(fieldData)   what the form's headline says — a review's is the
 *                      reviewer's name, not its internal `name`
 *   nameCard           `{ title, showName, slugLabel, slugHelp }` — the fixed
 *                      card above the sections: a review's shows only its
 *                      locked address, a question's is called "Question" and
 *                      its address an identifier
 *   pickLabel(fieldData)   how the Home picks screen labels an Item of this
 *                      collection in its menus — a review by reviewer and rating
 *   listNeedsHome      the list GET reads content/site/home.json and hands
 *                      it to rowExtras as `ctx.home` (a review's row says
 *                      whether the home page pins it)
 *   listNeedsRefs      the list GET loads the reference index and returns
 *                      `referenceChoices`, so rows can carry `placements` and
 *                      the list can filter by page (a FAQ)
 *   rowExtras(item, ctx)   extra keys on a list row — `hints: string[]` shown
 *                      after the name, and whatever the columns and filters
 *                      below read (`pinned`, `sameAs`, `placements`, `closed`).
 *                      `ctx` is `{ items, schema, home, refs, now }`; each may
 *                      be missing (the pending list summarises without them),
 *                      and a hook that needs one returns nothing without it.
 *   listColumns[].cell(row, ctx)   how a column is drawn, when its `kind` is
 *                      not enough: a string, or `{ text, label?, pill?: {text,
 *                      tone} }` where tone is bad | warn | info | ok
 *   listFilters        `[{ key, label, every, choices(data), keep(row, value) }]`
 *                      — filters beyond status and `optionFilter`; `choices`
 *                      gets the list payload (for the FAQ's page filter, the
 *                      `referenceChoices`), and `?key=value` in the address
 *                      preselects one, which is how a dashboard card lands on
 *                      the rows it counted
 *   listNote(rows)     one line under the list, or null ("N questions appear
 *                      only on /questions")
 *   emptyText          what an empty list says
 *   rowActions         `["copy"]` — a Copy button on each row, which starts a
 *                      new Item from it (`from`)
 *   attention          `[{ kind, label(n), query }]` — what the dashboard
 *                      counts for this collection: the rows `rowMatches` keeps
 *                      for `query`, which is also the address the card links
 *                      to. Drafts are counted for every collection without
 *                      being listed here.
 *
 * `defineCollection` fills the defaults so an entry says only what is special.
 */

import { placementsOf } from "../../lib/faq-placement.js";
import { SHORT_QUOTE_CHARS, isLive, normaliseQuestion, plainText } from "../../lib/content-rules.mjs";
import { MANDATORY, RULES, decorativeFields } from "./forms.js";
import { formatDay } from "./dates.js";

/**
 * Keys a "copy from" create never carries over, whatever the collection.
 *
 * Beyond the name, address and dates: a copied `canonical` would tell Google
 * the new page is a duplicate of the old one, and it would never be indexed;
 * a copied `seo-title` or `og-image` would show the old page's head in search
 * and on social; a copied `featured` makes a second featured Item, and a
 * copied `order` a tie in the list.
 */
export const DEFAULT_COPY_EXCLUDES = [
  "name",
  "slug",
  "published-on",
  "closing-date",
  "canonical",
  "seo-title",
  "og-image",
  "featured",
  "order",
];

/** Where a row's thumbnail is looked for, in order, unless the entry says. */
export const DEFAULT_IMAGE_FIELDS = ["photo", "main-image", "picture"];

/** Why a page comes down, when the collection has nothing more specific to say. */
export const DEFAULT_UNPUBLISH_REASONS = ["No longer current", "Left the clinic", "Published by mistake", "Replaced by another page"];

/**
 * @typedef {object} NameCard
 * @property {string} title
 * @property {boolean} showName   false when a section owns the name (a review's `client-name`)
 * @property {string} slugLabel
 * @property {string|null} slugHelp   said instead of the address help, when the address is not a page
 * @property {boolean} slugLocked  true when the panel numbers the address at creation and nobody
 *   changes it after, draft or not (a review): the form shows it disabled and the API ignores it
 */

/** The default name card: the Item's name, and its web address. @type {NameCard} */
export const DEFAULT_NAME_CARD = {
  title: "Name and address",
  showName: true,
  slugLabel: "Web address",
  slugHelp: null,
  slugLocked: false,
};

/**
 * @typedef {object} ListColumn
 * @property {string} key
 * @property {string} label
 * @property {"option"|"date"|"number"} kind
 * @property {string} [field]
 * @property {(row: object, ctx: {schema: object, now: Date}) => string|Cell} [cell]
 */

/**
 * @typedef {object} Cell
 * @property {string} text
 * @property {string} [label]   read aloud, or shown on hover, when `text` is a glyph
 * @property {{text: string, tone: "bad"|"warn"|"info"|"ok"}|null} [pill]
 */

/**
 * @typedef {object} ListFilter
 * @property {string} key
 * @property {string} label
 * @property {string} every    the choice that keeps every row
 * @property {(data: object) => {value: string, label: string}[]} choices
 * @property {(row: object, value: string) => boolean} keep
 */

/**
 * @typedef {object} AttentionSpec
 * @property {string} kind
 * @property {(count: number) => string} label
 * @property {Record<string, string>} query   the filters, as the list's address carries them
 */

/**
 * @typedef {object} Pin
 * @property {string} file   the content file, repo-relative ("content/site/home.json")
 * @property {(json: any) => string[]} slugs   the slugs it pins, [] for a file of another shape
 * @property {string} where  where the Item is shown: "on the home page"
 * @property {string} way    what to do first: "Take it off the Home picks screen first, then come back."
 */

/**
 * @typedef {object} CollectionEntry
 * @property {string} slug
 * @property {string} one
 * @property {string} many
 * @property {string} blurb
 * @property {string|null} urlPrefix
 * @property {((fieldData: object, refs?: object) => string|null)|null} whereShown
 * @property {((fieldData: object, refs?: object) => {url: string, title: string}[])|null} placements
 * @property {string|null} defaultDestination
 * @property {boolean} seoRows
 * @property {string[]} neighbours
 * @property {string} nameLabel
 * @property {string} createLabel
 * @property {string} createPlaceholder
 * @property {(fieldData: object) => string} seoTitle
 * @property {string} descriptionField
 * @property {ListColumn[]} listColumns
 * @property {{field: string, label: string, every: string}|null} optionFilter
 * @property {Reorder|null} reorder
 * @property {string[]} imageFields
 * @property {string[]} mandatory
 * @property {object[]} rules
 * @property {string[]} decorative
 * @property {string[]} copyExcludes
 * @property {(collection: string, fields: object, existingItems: object[], schema?: object) => object} createDefaults
 * @property {((name: string, existingItems: object[]) => string)|null} createSlug
 * @property {(fieldData: object, existing: object) => object} beforeSave
 * @property {string[]} mandatoryForPreview
 * @property {Pin[]} pins
 * @property {string[]} unpublishReasons
 * @property {string} listNameLabel
 * @property {(fieldData: object) => string} title
 * @property {NameCard} nameCard
 * @property {(fieldData: object) => string} pickLabel
 * @property {boolean} listNeedsHome
 * @property {boolean} listNeedsRefs
 * @property {(item: object, ctx: object) => object} rowExtras
 * @property {ListFilter[]} listFilters
 * @property {(rows: object[]) => string|null} listNote
 * @property {string} emptyText
 * @property {string[]} rowActions
 * @property {AttentionSpec[]} attention
 */

/**
 * @typedef {object} Reorder
 * @property {string} field      the Number written, 1..n
 * @property {string} groupBy    the field the list is split by; the row carries it
 * @property {string} title
 * @property {string} help
 * @property {string} [pickLabel]   the words before the group menu ("Order on:")
 * @property {(data: object) => {id: string, name: string}[]} [groups]
 * @property {(row: object, groupId: string, schema: object) => boolean} [member]
 * @property {(a: object, b: object) => number} [compare]
 * @property {string} [emptyText]
 */

/**
 * An entry with every default filled in.
 * @param {Omit<Partial<CollectionEntry>, "nameCard"> & {slug: string, one: string, many: string, nameCard?: Partial<NameCard>}} entry
 * @returns {CollectionEntry}
 */
export function defineCollection(entry) {
  const { slug } = entry;
  // An entry that lists every page an Item is on has said where it is shown.
  const whereShown = entry.whereShown ?? (entry.placements ? (d, refs) => entry.placements(d, refs)[0]?.url ?? null : null);
  return {
    blurb: "",
    urlPrefix: null,
    placements: null,
    defaultDestination: null,
    seoRows: true,
    neighbours: [],
    nameLabel: "Name",
    createLabel: "The name",
    createPlaceholder: "",
    seoTitle: (d) => d.name || "",
    descriptionField: "description",
    listColumns: [],
    optionFilter: null,
    reorder: null,
    imageFields: DEFAULT_IMAGE_FIELDS,
    mandatory: MANDATORY[slug] || [],
    rules: RULES[slug] || [],
    decorative: decorativeFields(slug),
    copyExcludes: DEFAULT_COPY_EXCLUDES,
    createDefaults: () => ({}),
    createSlug: null,
    beforeSave: (fieldData) => fieldData,
    mandatoryForPreview: [],
    pins: [],
    unpublishReasons: DEFAULT_UNPUBLISH_REASONS,
    listNameLabel: "Name",
    title: (d) => d.name || "",
    pickLabel: (d) => d.name || "",
    listNeedsHome: false,
    listNeedsRefs: false,
    rowExtras: () => ({}),
    listFilters: [],
    listNote: () => null,
    emptyText: "Nothing matches.",
    rowActions: [],
    attention: [],
    ...entry,
    nameCard: { ...DEFAULT_NAME_CARD, ...(entry.nameCard || {}) },
    whereShown,
  };
}

/* ------------------------------------------------ rows, filtered and drawn */

/**
 * Whether a list row passes a set of filters — the list screen's and the
 * dashboard's one answer to "which rows", so a card that says "3 reviews
 * without a consent date" links to a list showing those three.
 *
 * `query` is `{ status, <optionFilter.field>, <listFilters[].key> }`, each
 * optional; an empty value or "all" keeps every row. The Option filter is
 * matched by the option's *name*, which is what the address and the select
 * carry.
 *
 * @param {CollectionEntry} entry
 * @param {object} row
 * @param {Record<string, string>} query
 * @param {object} [schema]  for the Option filter's names
 */
export function rowMatches(entry, row, query, schema = null) {
  for (const [key, value] of Object.entries(query || {})) {
    if (!value || value === "all") continue;
    if (key === "status") {
      if (row.status !== value) return false;
      continue;
    }
    if (entry.optionFilter && key === entry.optionFilter.field) {
      if (optionName(schema, key, row[key]) !== value) return false;
      continue;
    }
    const filter = entry.listFilters.find((f) => f.key === key);
    if (filter && !filter.keep(row, value)) return false;
  }
  return true;
}

/** `status=published&consent=missing` — a query as the list's address carries it. */
export const queryString = (query) =>
  Object.entries(query || {})
    .filter(([, value]) => value && value !== "all")
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join("&");

/**
 * What identifies one list screen: the collection and the filters its address
 * carries. The list reads its filters from the address once, when it mounts,
 * so a screen keyed by this starts afresh when the address changes — from a
 * dashboard card's `#/faq?status=published&page=unplaced` to the nav's `#/faq`.
 */
export const listKey = (collection, query) => `${collection}?${queryString(query)}`;

/** The text and pill a column shows for a row, whatever the column declares. */
export function cellOf(column, row, ctx) {
  if (column.cell) {
    const drawn = column.cell(row, ctx);
    return typeof drawn === "string" ? { text: drawn } : drawn || { text: "" };
  }
  const value = row[column.key];
  if (column.kind === "date") return { text: formatDay(value) || "—" };
  if (column.kind === "number") return { text: typeof value === "number" ? String(value) : "—" };
  return { text: optionName(ctx?.schema, column.field || column.key, value) || "—" };
}

/* ------------------------------------------- what the new entries lean on */

/** The Live Items of a loaded neighbour, as a reference list wants them. */
const liveOf = (refs, collection) =>
  (refs?.collections?.[collection]?.items || []).filter(isLive).map((i) => ({ id: i.id, slug: i.fieldData.slug, name: i.fieldData.name }));

/**
 * The pages one FAQ appears on — the site's own rule (lib/faq-placement.js),
 * which wants the category's *name* and the Live Assessments and Services;
 * the reference index has all three once `neighbours` are loaded. Nothing
 * without it: the browser's list row cannot tell, and says so with a dash.
 */
function faqPlacements(fieldData, refs) {
  const schema = refs?.collections?.faq?.collection;
  if (!schema) return [];
  const faq = {
    category: optionName(schema, "category", fieldData.category) || null,
    assessment: fieldData.assessment,
    service: fieldData.service,
  };
  return placementsOf(faq, liveOf(refs, "assessment"), liveOf(refs, "service"));
}

/** `testimonial-<N>`: the next number after the highest one taken, drafts included. */
function nextTestimonialSlug(_name, existingItems) {
  const taken = existingItems.map((i) => /^testimonial-(\d+)$/.exec(String(i.fieldData?.slug || ""))).filter(Boolean).map((m) => Number(m[1]));
  return `testimonial-${(taken.length ? Math.max(...taken) : 0) + 1}`;
}

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const hasText = (value) => Boolean(String(value ?? "").trim());
const isPast = (iso, now) => Boolean(iso) && !Number.isNaN(Date.parse(iso)) && Date.parse(iso) < now.getTime();
const isAhead = (iso, now) => Boolean(iso) && !Number.isNaN(Date.parse(iso)) && Date.parse(iso) > now.getTime();

/**
 * The quote and the reviewer, as one key: two reviews are the same review when
 * both match, and a quote under `SHORT_QUOTE_CHARS` is exempt — "Five Star
 * Clinic" is three different people saying the same three words.
 */
function reviewKey(fieldData) {
  const quote = plainText(fieldData?.["testimonial-content"] || "").replace(/\s+/g, " ").trim().toLowerCase();
  if (quote.length < SHORT_QUOTE_CHARS) return null;
  const who = String(fieldData?.["client-name"] || "").replace(/\s+/g, " ").trim().toLowerCase();
  return who ? `${who}\n${quote}` : null;
}

/** Another review in the file with the same quote and reviewer, or null. */
function sameReviewAs(item, items) {
  const key = reviewKey(item.fieldData);
  if (!key) return null;
  return (items || []).find((other) => other.id !== item.id && reviewKey(other.fieldData) === key) || null;
}

/** Another Live question asking the same thing as this Live one, or null. */
function sameQuestionAs(item, items) {
  if (!isLive(item)) return null;
  const key = normaliseQuestion(item.fieldData?.name);
  if (!key) return null;
  return (items || []).find((other) => other.id !== item.id && isLive(other) && normaliseQuestion(other.fieldData?.name) === key) || null;
}

/** The stars a rating draws in a list: five glyphs at most, or a dash. */
function starsCell(row) {
  const n = Number(row.rating);
  if (!Number.isInteger(n) || n < 1) return { text: "—" };
  return { text: "★".repeat(Math.min(n, 5)), label: `${n} of 5` };
}

/** The pages a FAQ row is on, named — or "/questions" when that is the only one. */
function shownOnCell(row) {
  const pages = row.placements;
  if (!Array.isArray(pages)) return { text: "—" };
  if (pages.length === 1 && pages[0].url === "/questions/") return { text: "/questions" };
  return { text: pages.map((p) => (p.url === "/questions/" ? "/questions" : p.title)).join(", ") };
}

/** The FAQ's page filter: the Live assessments, then the services, then /questions. */
function faqPageChoices(data) {
  const refs = data?.referenceChoices || {};
  const pages = (list, prefix) => (list || []).map((r) => ({ value: `${prefix}/${r.slug}/`, label: r.name }));
  return [
    ...pages(refs.assessment, "/assessment"),
    ...pages(refs.service, "/service"),
    { value: "/counselling-psychotherapy/", label: "Counselling & Psychotherapy" },
    { value: "/questions/", label: "/questions" },
    { value: "unplaced", label: "/questions only, because no page claims it" },
  ];
}

/**
 * The order an assessment page shows its questions in — the same comparator
 * as `faqsFor()` in src/assessment/assessment.11tydata.js: `sort` ascending
 * with the empty ones *first* (Webflow's rule for an empty number), then by
 * creation date. The Reorder card starts from this, so what it shows is the
 * page as it is today and not the panel's idea of it; a Save then writes
 * 1..n over whatever mixture of numbers and blanks was there.
 */
function faqPageOrder(a, b) {
  const rankA = typeof a.sort === "number" ? a.sort : -1;
  const rankB = typeof b.sort === "number" ? b.sort : -1;
  if (rankA !== rankB) return rankA - rankB;
  return String(a.createdOn).localeCompare(String(b.createdOn));
}

/**
 * Whether the assessment page `groupId` shows this question: Live is the
 * caller's check; here, the Assessment kind and attached — the band's own
 * filter. A General question attached to an assessment is on /questions,
 * not on that page, so it is not in the page's order.
 */
function faqOnAssessmentPage(row, groupId, schema) {
  return row.category === optionId(schema, "category", "Assessment") && (row.assessment || []).includes(groupId);
}

/** The home page's picks, which the Home picks screen edits. */
const HOME_FILE = "content/site/home.json";
const HOME_PICKS_WAY = "Take it off the Home picks screen first, then come back.";
/** `value` if it is a list, else none: a pins file of another shape pins nothing here, and check-content says what is wrong with it. */
const listOf = (value) => (Array.isArray(value) ? value : []);

/** @type {CollectionEntry[]} in the order the nav shows them. */
export const COLLECTIONS = [
  defineCollection({
    slug: "provider",
    one: "Provider",
    many: "Providers",
    blurb: "The people on every provider card and page: /providers, About Us, the home page, the Assessment, Counselling and Service pages.",
    urlPrefix: "/provider",
    defaultDestination: "/providers/",
    // The home page's twelve faces and the About Us team (ADR-0025): the
    // build fails while either names a Provider that is not Live.
    pins: [
      {
        file: HOME_FILE,
        slugs: (home) => listOf(home?.teamTabs).flatMap((tab) => listOf(tab?.faces)),
        where: "on the home page",
        way: HOME_PICKS_WAY,
      },
      {
        file: "content/site/about.json",
        slugs: (about) => [
          ...listOf(about?.teamCards).map((card) => (typeof card === "string" ? card : card?.slug)),
          ...listOf(about?.teamColumns).flatMap((column) => listOf(column?.people)),
        ],
        where: "in the About Us page's team",
        way:
          "The panel does not edit that list (content/site/about.json): ask whoever looks after the website " +
          "to take them off it first, then come back.",
      },
    ],
    neighbours: ["assessment"],
    nameLabel: "Name",
    createLabel: "Their name",
    createPlaceholder: "Dr. Jane Doe",
    seoTitle: (d) => `${d.name} | Provider`,
    descriptionField: "meta-description-seo",
    listColumns: [{ key: "group", label: "Discipline", kind: "option", field: "group" }],
    optionFilter: { field: "group", label: "Discipline", every: "Every discipline" },
    reorder: {
      field: "order",
      groupBy: "group",
      title: "The order they appear in",
      help: "The Assessment pages' team lists, within each discipline. /providers follows the team map (content/site/team.json), not this order. Published people only.",
    },
  }),

  defineCollection({
    slug: "article",
    one: "Article",
    many: "Articles",
    blurb: "The blog.",
    urlPrefix: "/article",
    defaultDestination: "/blog/",
    neighbours: ["author", "article"],
    nameLabel: "Headline",
    createLabel: "The headline",
    createPlaceholder: "What families ask us about autism assessments",
    seoTitle: (d) => d["article-h1-title"] || d.name || "",
    descriptionField: "description",
    listColumns: [{ key: "publishedOn", label: "Published", kind: "date", field: "published-on" }],
  }),

  defineCollection({
    slug: "author",
    one: "Author",
    many: "Authors",
    blurb: "Who articles are written by.",
    urlPrefix: "/author",
    defaultDestination: "/blog/",
    neighbours: [],
    nameLabel: "Name",
    createLabel: "Their name",
    createPlaceholder: "Dr. Jane Doe",
    seoTitle: (d) => d.name || "",
    descriptionField: "bio-summary",
  }),

  defineCollection({
    slug: "testimonials",
    one: "Review",
    many: "Reviews",
    blurb: "What families have said, on /testimonials and the home page. Each needs the reviewer's consent on record before it is published.",
    // The item pages exist (`/testimonials/<slug>/`) but are noindex by
    // pattern and out of the sitemap, so there is no head to edit and no SEO
    // row; the redirect on unpublish still matters, because the pages are
    // linked from each other.
    urlPrefix: "/testimonials",
    seoRows: false,
    defaultDestination: "/testimonials/",
    neighbours: ["provider", "service", "assessment"],
    nameLabel: "Reviewer",
    listNameLabel: "Reviewer",
    createLabel: "The reviewer's name, as it will appear",
    createPlaceholder: "Sarah M.",
    // The name lives in "The review" (`client-name`, which `name` follows), so
    // the fixed card shows only the locked address, and the headline is the
    // reviewer as typed this sitting.
    title: (d) => d["client-name"] || d.name || "",
    // Brief §4.1: the address is locked from creation, not from publish —
    // `/testimonials/<slug>/`, home.json's picks and the next number all
    // count on `testimonial-<N>`.
    nameCard: { title: "Address", showName: false, slugLocked: true, slugHelp: "Numbered by the panel when the review is started. It cannot be changed." },
    pickLabel: (d) => `${d["client-name"] || d.name || ""} — ${plural(Number(d.rating) || 0, "star")}`,
    seoTitle: (d) => `${d["client-name"] || d.name || ""} | Testimonial by our patient`,
    descriptionField: "testimonial-content",
    listColumns: [
      { key: "rating", label: "Rating", kind: "number", field: "rating", cell: starsCell },
      {
        key: "source",
        label: "Source",
        kind: "option",
        field: "source",
        // The mark says the link to the original is on file; the text says
        // where the review came from.
        cell: (row, ctx) => ({
          text: optionName(ctx?.schema, "source", row.source) || "—",
          label: row.hasLink ? "The link to the original is on file" : undefined,
          pill: row.hasLink ? { text: "G", tone: "info" } : null,
        }),
      },
      {
        key: "consentOn",
        label: "Consent",
        kind: "date",
        field: "consent-on",
        cell: (row) => (row.consentOn ? { text: formatDay(row.consentOn) } : { text: "", pill: { text: "no consent date", tone: "bad" } }),
      },
    ],
    // What the list needs beyond the row: whether the home page pins it (one
    // read of home.json), whether another Item is the same review, and
    // whether the Google link is there.
    rowExtras: (item, ctx = {}) => {
      const d = item.fieldData || {};
      const pinned = Boolean(ctx.home) && (ctx.home.featuredReviews || []).includes(d.slug);
      const twin = sameReviewAs(item, ctx.items);
      return {
        pinned,
        sameAs: twin ? twin.fieldData.slug : null,
        hasLink: hasText(d["reviews-link-2"]),
        hints: [pinned ? "pinned on home" : null, twin ? `same text as ${twin.fieldData.slug}` : null].filter(Boolean),
      };
    },
    listFilters: [
      {
        key: "consent",
        label: "Consent",
        every: "Any consent",
        choices: () => [{ value: "missing", label: "No consent date" }],
        keep: (row, value) => value !== "missing" || !row.consentOn,
      },
    ],
    attention: [
      {
        kind: "consent",
        label: (n) => `${plural(n, "live review")} without a consent date`,
        query: { status: "published", consent: "missing" },
      },
    ],
    imageFields: ["users-image"],
    // A new review starts as the anonymised default (brief §4.1): the name
    // typed is the display name, five stars, from Google, and the
    // "unrelated reviewer" declaration left for a person to tick.
    createDefaults: (_collection, fields, _existing, schema) => ({
      "client-name": fields.name,
      rating: 5,
      source: optionId(schema, "source", "Google review") ?? null,
      "consent-scope": optionId(schema, "consent-scope", "First name and initial") ?? null,
    }),
    createSlug: nextTestimonialSlug,
    // The list, the home-picks picker and the commit subject all read `name`;
    // the reviewer's name is what they should say, on every save, not only
    // the first.
    beforeSave: (fieldData) => {
      const shown = String(fieldData["client-name"] ?? "").trim();
      return shown ? { ...fieldData, name: shown } : fieldData;
    },
    mandatoryForPreview: ["consent-on", "consent-scope", "consent-by"],
    pins: [
      {
        file: HOME_FILE,
        slugs: (home) => listOf(home?.featuredReviews),
        where: "on the home page",
        way: HOME_PICKS_WAY,
      },
    ],
    listNeedsHome: true,
    unpublishReasons: ["Withdrawn at the reviewer's request", "No longer current", "Published by mistake"],
  }),

  defineCollection({
    slug: "faq",
    one: "Question",
    many: "Questions",
    blurb: "The questions and answers on assessment pages, service pages, the counselling page and /questions. A question has no page of its own.",
    urlPrefix: null,
    placements: faqPlacements,
    neighbours: ["assessment", "service"],
    nameLabel: "Question",
    listNameLabel: "Question",
    nameCard: {
      title: "Question",
      slugLabel: "Identifier",
      slugHelp: "A question has no page of its own; this names it in the file and in previews. Locked once published.",
    },
    createLabel: "The question",
    createPlaceholder: "Do I need a doctor's referral to book an assessment?",
    seoTitle: (d) => d.name || "",
    descriptionField: "content",
    imageFields: [],
    listNeedsRefs: true,
    listColumns: [
      { key: "category", label: "Kind", kind: "option", field: "category" },
      { key: "placements", label: "Shown on", kind: "option", cell: shownOnCell },
      { key: "sort", label: "Position", kind: "number", field: "sort" },
    ],
    optionFilter: { field: "category", label: "Kind", every: "Every kind" },
    // Brief §5.2 "Order". The group is an assessment, not an option: the
    // Live assessments the list GET already returns as `referenceChoices`,
    // and a row is in one when that page shows it. Service pages stay
    // newest-first (ADR-0011), so there is nothing to order there.
    reorder: {
      field: "sort",
      groupBy: "assessment",
      title: "The order on an assessment page",
      help: "Live questions of the Assessment kind, on the page of the assessment chosen here. A question on two pages has one position, so moving it here moves it there too.",
      pickLabel: "Order on:",
      groups: (data) => (data?.referenceChoices?.assessment || []).map(({ id, name }) => ({ id, name })),
      member: faqOnAssessmentPage,
      compare: faqPageOrder,
      emptyText: "No live question of the Assessment kind is attached to this assessment yet.",
    },
    // Where the row is shown is the site's own rule, given the reference
    // index the list GET loads for this collection; `unplaced` is the
    // /questions-by-default case the dashboard counts — on no page but
    // /questions, and not there by its kind.
    rowExtras: (item, ctx = {}) => {
      const d = item.fieldData || {};
      const placements = ctx.refs ? faqPlacements(d, ctx.refs) : null;
      const onlyQuestions = Array.isArray(placements) && placements.length === 1 && placements[0].url === "/questions/";
      const kind = ctx.schema ? optionName(ctx.schema, "category", d.category) : "";
      const twin = sameQuestionAs(item, ctx.items);
      return {
        // The Reorder card's tie-break for an empty `sort`, as the page's is.
        createdOn: item.createdOn || null,
        placements,
        onlyQuestions,
        unplaced: onlyQuestions && kind !== "General",
        sameAs: twin ? twin.fieldData.slug : null,
        hints: [twin ? `same question as ${twin.fieldData.slug}` : null].filter(Boolean),
      };
    },
    listFilters: [
      {
        key: "page",
        label: "Page",
        every: "Every page",
        choices: faqPageChoices,
        keep: (row, value) => (value === "unplaced" ? row.unplaced === true : (row.placements || []).some((p) => p.url === value)),
      },
      {
        key: "twin",
        label: "Duplicates",
        every: "Asked once or twice",
        choices: () => [{ value: "yes", label: "Asked twice" }],
        keep: (row, value) => value !== "yes" || Boolean(row.sameAs),
      },
    ],
    listNote: (rows) => {
      const n = rows.filter((row) => row.status === "published" && row.onlyQuestions).length;
      return n ? `${plural(n, "question")} appear${n === 1 ? "s" : ""} only on /questions.` : null;
    },
    attention: [
      {
        kind: "unplaced",
        label: (n) => `${plural(n, "question")} on /questions only, because no page claims ${n === 1 ? "it" : "them"}`,
        query: { status: "published", page: "unplaced" },
      },
      {
        kind: "duplicate",
        label: (n) => `${plural(n, "question")} asked twice`,
        query: { status: "published", twin: "yes" },
      },
    ],
    // Pageless: nothing is retired on unpublish, so no reason is asked for.
    unpublishReasons: [],
  }),

  defineCollection({
    slug: "careers",
    one: "Posting",
    many: "Postings",
    blurb: "Job postings on /careers. A posting must state its wage (BC Pay Transparency Act) before it is published.",
    urlPrefix: "/careers",
    defaultDestination: "/careers/",
    neighbours: [],
    nameLabel: "Job title",
    listNameLabel: "Title",
    createLabel: "The job title",
    createPlaceholder: "Clinic Administrative Support Supervisor",
    seoTitle: (d) => `${d.name || ""} | All Brains Clinic`,
    descriptionField: "short-description",
    imageFields: [],
    listColumns: [
      { key: "publishedOn", label: "Posted", kind: "date", field: "published-on" },
      {
        key: "closingDate",
        label: "Closes",
        kind: "date",
        field: "closing-date",
        cell: (row) => ({
          text: formatDay(row.closingDate) || "—",
          pill: row.closed ? { text: "closed", tone: "bad" } : row.scheduled ? { text: "scheduled", tone: "info" } : null,
        }),
      },
    ],
    // Closed once the closing day has passed (lib/careers-closed.js draws the
    // page that way at the next build); scheduled while the posting date is
    // still to come on a published posting.
    rowExtras: (item, ctx = {}) => {
      const d = item.fieldData || {};
      const now = ctx.now || new Date();
      const live = isLive(item);
      return {
        closed: live && isPast(d["closing-date"], now),
        scheduled: live && isAhead(d["published-on"], now),
        hints: [],
      };
    },
    listFilters: [
      {
        key: "closing",
        label: "Closing",
        every: "Open or closed",
        choices: () => [
          { value: "closed", label: "Past the closing date" },
          { value: "scheduled", label: "Scheduled" },
        ],
        keep: (row, value) => (value === "closed" ? row.closed === true : value === "scheduled" ? row.scheduled === true : true),
      },
    ],
    emptyText: "No postings. /careers/ shows its no-openings message.",
    rowActions: ["copy"],
    attention: [
      {
        kind: "closed",
        label: (n) => `${plural(n, "posting")} past ${n === 1 ? "its" : "their"} closing date — unpublish?`,
        query: { status: "published", closing: "closed" },
      },
      {
        kind: "scheduled",
        label: (n) => `${plural(n, "posting")} scheduled for a later date`,
        query: { status: "published", closing: "scheduled" },
      },
    ],
    unpublishReasons: ["Position filled", "Posting closed", "Published by mistake"],
  }),
];

/* ------------------------------------------------------------ the lookup */

/**
 * The registry in force. Tests swap it to register a collection that does not
 * exist yet — a pageless one, one with a rule — and put it back afterwards;
 * nothing else should ever call `overrideRegistry`.
 */
let active = COLLECTIONS;

/**
 * Replace the registry, and get back the function that restores it.
 * @param {CollectionEntry[]} entries
 * @returns {() => void}
 */
export function overrideRegistry(entries) {
  const previous = active;
  active = entries;
  return () => {
    active = previous;
  };
}

/** Every registered collection, in nav order. */
export const registry = () => active;

/** The slugs the panel edits. */
export const collectionSlugs = () => active.map((c) => c.slug);

/** One entry, or null for a collection the panel does not edit. */
export const collectionBySlug = (slug) => active.find((c) => c.slug === slug) || null;

/**
 * Where an Item of a collection is served, without the trailing slash — or
 * null when its Items have no page of their own.
 */
export function itemUrl(collection, slug) {
  const entry = collectionBySlug(collection);
  return entry?.urlPrefix ? `${entry.urlPrefix}/${slug}` : null;
}

/**
 * An Option field stores the option's id; this is the name for it.
 * @param {object} schema
 * @param {string} slug     the Option field
 * @param {string} value    the stored id
 */
export function optionName(schema, slug, value) {
  const field = (schema?.fields || []).find((f) => f.slug === slug);
  return (field?.validations?.options || []).find((o) => o.id === value)?.name || value || "";
}

/** The names of an Option field's choices, in the schema's order. */
export function optionNames(schema, slug) {
  const field = (schema?.fields || []).find((f) => f.slug === slug);
  return (field?.validations?.options || []).map((o) => o.name);
}

/** The id of an Option field's choice, by name. */
export function optionId(schema, slug, name) {
  const field = (schema?.fields || []).find((f) => f.slug === slug);
  return (field?.validations?.options || []).find((o) => o.name === name)?.id;
}

/* ------------------------------------------------------ the Reorder card */

/**
 * The groups an entry's Reorder card can put in order, `{ id, name }` each:
 * the entry's own list when it has one, else the options of `groupBy`.
 * @param {CollectionEntry} entry
 * @param {object} data   the list payload (`schema`, `items`, `referenceChoices`)
 */
export function reorderGroups(entry, data) {
  const spec = entry?.reorder;
  if (!spec) return [];
  if (spec.groups) return spec.groups(data);
  const field = (data?.schema?.fields || []).find((f) => f.slug === spec.groupBy);
  return (field?.validations?.options || []).map((o) => ({ id: o.id, name: o.name }));
}

/** Whether a row is in one group: the entry's rule, else the option's id. */
function inGroup(spec, row, groupId, schema) {
  return spec.member ? spec.member(row, groupId, schema) : row[spec.groupBy] === groupId;
}

/**
 * The published rows of one group in the order the site shows them today,
 * which is what the card starts from. Published only: a draft has no place
 * on the page, and a number written on it would surface the moment it is
 * published. The default order is the field ascending with the blanks last,
 * then by name; an entry whose page orders differently says so in `compare`.
 * @param {CollectionEntry} entry
 * @param {object} data
 * @param {string} groupId
 */
export function reorderMembers(entry, data, groupId) {
  const spec = entry?.reorder;
  if (!spec || !groupId) return [];
  const compare =
    spec.compare ||
    ((a, b) => (a[spec.field] ?? 9999) - (b[spec.field] ?? 9999) || String(a.name).localeCompare(String(b.name)));
  return (data?.items || [])
    .filter((row) => row.status === "published" && inGroup(spec, row, groupId, data?.schema))
    .sort(compare);
}

/**
 * The names of the other groups a row is also in — the pages its one position
 * also moves it on. Empty for a group that is an option, since a row has one.
 * @param {CollectionEntry} entry
 * @param {object} data
 * @param {object} row
 * @param {string} groupId   the group being ordered, left out
 */
export function reorderElsewhere(entry, data, row, groupId) {
  const spec = entry?.reorder;
  if (!spec?.member) return [];
  return reorderGroups(entry, data)
    .filter((group) => group.id !== groupId && spec.member(row, group.id, data?.schema))
    .map((group) => group.name);
}
